import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { isUndefined } from 'lodash'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useFarmContext } from 'src/Elements/Farm'
import { PviTracker, SolarModule } from 'src/Elements/SystemSettings/SystemSettingsUtils'
import { useSnackbarMessageBasedOnSoftwareVersion } from 'src/Libs/home-power-station'
import { updateSolarModules } from 'src/State/SystemSettings/SystemSettingActions'
import { getAllSolarModules } from '../utils'

interface UseSolarReturnType {
  solarModules: SolarModule[]
  onChangeTracker: (storageId: string | undefined, listIndex: number, pviIndex: number, pviTracker: PviTracker) => void
  activeTracker: number
  isLoading: boolean
  storageIdsWithError: string[]
}

/**
 * A hook that handles the state of solar-modules (pv-inverter).
 * Provides the current solar-modules. Further, each solar-module has a pv-tracker. This hook provides the active tracker as well as a function to update it.
 *
 * @param storageId: id of currently selected storage
 * @param storageIdServer id of server if installation is a farm (else undefined)
 * @returns
 */
const useSolar = (storageId?: string, storageIdServer?: string): UseSolarReturnType => {
  const authFetch = useAuthFetchWithResponse()
  const dispatch = useDispatch()
  const { t } = useCustomTranslation()

  const relevantStorageId = storageId ?? storageIdServer

  const { isFarmMasterSelected } = useFarmContext()
  const [activeTracker, setActiveTracker] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [storageIdsWithError, setStorageIdsWithError] = useState<string[]>([])
  const { enqueueErrorStack } = useSnackbar()
  const pageName = t('framework.solar')
  const accordionName = t('systemSettings.solar.settings.title')
  const { handleDisplaySnackbarMessageBasedOnSoftwareVersion } = useSnackbarMessageBasedOnSoftwareVersion()

  const [solarModules, setSolarModules] = useState<SolarModule[]>([])

  const currentModule = useMemo(() => {
    return solarModules?.find(solarModule => solarModule.storageId === storageId)
  }, [solarModules, storageId])

  useEffect(() => {
    if (currentModule?.storageId) {
      const activeTrackerIndex = currentModule.pviTracker - 1
      setActiveTracker(activeTrackerIndex)
    }
  }, [currentModule])

  useEffect(() => {
    setSolarModules([])
    dispatch(updateSolarModules([]))
    setStorageIdsWithError([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!isUndefined(isFarmMasterSelected)) {
      getAllSolarModules(relevantStorageId, isFarmMasterSelected, authFetch)
        .then(module => {
          setSolarModules(module)
          dispatch(updateSolarModules(module))
        })
        .catch(error => {
          console.error(error)
          const storageIds = [...storageIdsWithError]
          if (relevantStorageId) storageIds.push(relevantStorageId)
          setStorageIdsWithError(storageIds)
          enqueueErrorStack(t('settings.pv-inverter.load.error'))
        })
        .finally(() => setIsLoading(false))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relevantStorageId, isFarmMasterSelected])

  const onChangeTracker = useCallback(
    (storageId: string | undefined, listIndex: number, pviIndex: number, pviTracker: PviTracker): void => {
      setActiveTracker(listIndex)
      authFetch<SolarModule[]>({
        method: 'PUT',
        path: `storages/${storageId}/hardware/pv-inverter/overview/${pviIndex}`,
        payload: { pviTracker: pviTracker },
      })
        .then(() => {
          if (!isUndefined(isFarmMasterSelected)) {
            handleDisplaySnackbarMessageBasedOnSoftwareVersion({ pageName, accordionName })
            return getAllSolarModules(relevantStorageId, isFarmMasterSelected, authFetch)
          }
        })
        .then(module => {
          if (module) {
            setSolarModules(module)
            dispatch(updateSolarModules(module))
          }
        })
        .catch(error => {
          setActiveTracker(activeTracker)
          console.error(error)
          enqueueErrorStack(t('common.systemSettings.api.error.failed-form-submission', { pageName, accordionName }))
        })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [authFetch, isFarmMasterSelected, t, handleDisplaySnackbarMessageBasedOnSoftwareVersion, relevantStorageId, activeTracker]
  )

  return { solarModules, onChangeTracker, activeTracker, isLoading, storageIdsWithError }
}

export default useSolar
